/**
 * Hotspot Markers
 *
 * Positions .hero-marker elements over the visible portion of an image inside
 * a [data-markers-container]. Accounts for object-fit: cover cropping so
 * markers stay glued to the same point on the underlying image regardless of
 * the container aspect ratio.
 *
 * Markup contract:
 *   <div data-markers-container>
 *     ... <img data-markers-image src="..."> ...
 *     <div class="product-markers">
 *       <div class="hero-marker"
 *            data-pos-x="50" data-pos-y="40"
 *            data-direction="bottom-right" data-line-style="1-kink"
 *            data-pos-x-mobile="50" data-pos-y-mobile="40"
 *            data-direction-mobile="bottom-right" data-line-style-mobile="1-kink">
 *         <button class="hero-marker__dot" aria-expanded="false">...</button>
 *         ...
 *       </div>
 *     </div>
 *   </div>
 *
 * Desktop:
 *   - Hover on the dot reveals the line + card (CSS).
 *   - Markers use the non-suffixed data-pos-x / data-pos-y / data-direction.
 *
 * Mobile (< 750px):
 *   - Tap on the dot toggles `.is-open` on the marker — CSS reveals the
 *     card. Only one marker per container can be open at a time; opening
 *     another auto-closes the previous one. Tapping anywhere outside an
 *     open marker also closes it.
 *   - Markers use the data-pos-x-mobile / data-pos-y-mobile / data-direction-
 *     mobile / data-line-style-mobile values when they're set, falling back
 *     to the desktop values when blank.
 */

(function () {
  var MOBILE_BREAKPOINT = 750;

  function isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  function getImageVisibleRect(media) {
    var img = media.querySelector('img, video');
    if (!img) {
      return { offsetX: 0, offsetY: 0, visibleW: media.offsetWidth, visibleH: media.offsetHeight };
    }

    var containerW = media.offsetWidth;
    var containerH = media.offsetHeight;
    var naturalW = img.naturalWidth || img.videoWidth || containerW;
    var naturalH = img.naturalHeight || img.videoHeight || containerH;

    var containerRatio = containerW / containerH;
    var imageRatio = naturalW / naturalH;

    var renderedW;
    var renderedH;

    if (imageRatio > containerRatio) {
      renderedH = containerH;
      renderedW = containerH * imageRatio;
    } else {
      renderedW = containerW;
      renderedH = containerW / imageRatio;
    }

    var offsetX = (containerW - renderedW) / 2;
    var offsetY = (containerH - renderedH) / 2;

    return { offsetX: offsetX, offsetY: offsetY, visibleW: renderedW, visibleH: renderedH };
  }

  // Read the active position/direction/line-style for this breakpoint and
  // sync them onto the marker. Mobile attrs fall back to desktop when blank.
  function applyBreakpointAttrs(marker) {
    var mobile = isMobile();
    var px = mobile ? marker.dataset.posXMobile : marker.dataset.posX;
    var py = mobile ? marker.dataset.posYMobile : marker.dataset.posY;
    var dir = mobile ? marker.dataset.directionMobile : marker.dataset.direction;
    var style = mobile ? marker.dataset.lineStyleMobile : marker.dataset.lineStyle;

    // Fall back to desktop values when mobile-specific values are missing
    if (!px || px === '') px = marker.dataset.posX;
    if (!py || py === '') py = marker.dataset.posY;
    if (!dir || dir === '') dir = marker.dataset.direction;
    if (!style || style === '') style = marker.dataset.lineStyle;

    marker.dataset.activePosX = px;
    marker.dataset.activePosY = py;
    marker.setAttribute('data-direction', dir || 'bottom-right');
    if (style === '2-kink') {
      marker.classList.add('hero-marker--2kink');
    } else {
      marker.classList.remove('hero-marker--2kink');
    }
  }

  /**
   * On mobile, flip the marker's `data-direction` if the card would
   * overflow the container in the current direction. We compute the
   * card's would-be bounding box from the marker's pixel position +
   * the line/card offsets defined in `assets/hotspot-markers.css` @media
   * mobile block, then swap left↔right and/or top↔bottom as needed.
   *
   * Reach math (theme uses default `1rem = 16px`):
   *   1-kink horizontal: line 3rem + card 11rem  = 14rem = 224px
   *   2-kink horizontal: card 11rem - 1.6rem inset + 3rem leg = ~12.4rem ≈ 198px
   *   1-kink vertical:   line 2.5rem + card height ~5rem = ~120px
   *   2-kink vertical:   line 2.5rem + leg 2rem + card height ~5rem = ~152px
   *
   * Plus an 8px safety buffer so the card never touches the edge.
   */
  function autoFlipMobileDirection(marker, container) {
    if (!isMobile()) return;

    var markerLeftPx = parseFloat(marker.style.left) || 0;
    var markerTopPx = parseFloat(marker.style.top) || 0;
    var containerW = container.offsetWidth;
    var containerH = container.offsetHeight;

    var dir = marker.getAttribute('data-direction') || 'bottom-right';
    var isTwoKink = marker.classList.contains('hero-marker--2kink');

    // px reach values — must stay in sync with the @media block in
    // assets/hotspot-markers.css. Slight buffer (8px) so we don't sit
    // flush against the edge.
    var horizontalReach = (isTwoKink ? 198 : 224) + 8;
    var verticalReach = (isTwoKink ? 152 : 120) + 8;

    var hasRight = dir.indexOf('right') !== -1;
    var hasLeft = dir.indexOf('left') !== -1;
    var hasTop = dir.indexOf('top') !== -1;
    var hasBottom = dir.indexOf('bottom') !== -1;

    var flipH = false;
    var flipV = false;

    if (hasRight && markerLeftPx + horizontalReach > containerW) flipH = true;
    if (hasLeft && markerLeftPx - horizontalReach < 0) flipH = true;
    if (hasBottom && markerTopPx + verticalReach > containerH) flipV = true;
    if (hasTop && markerTopPx - verticalReach < 0) flipV = true;

    if (!flipH && !flipV) return;

    var newDir = dir;
    if (flipH) {
      newDir = hasRight ? newDir.replace('right', 'left') : newDir.replace('left', 'right');
    }
    if (flipV) {
      newDir = hasTop ? newDir.replace('top', 'bottom') : newDir.replace('bottom', 'top');
    }

    marker.setAttribute('data-direction', newDir);
  }

  function closeAllInContainer(container, except) {
    container.querySelectorAll('.hero-marker.is-open').forEach(function (m) {
      if (m === except) return;
      m.classList.remove('is-open');
      var dot = m.querySelector('.hero-marker__dot');
      if (dot) dot.setAttribute('aria-expanded', 'false');
    });
  }

  function bindTapToggles(container, markers) {
    markers.forEach(function (marker) {
      var dot = marker.querySelector('.hero-marker__dot');
      if (!dot || dot.dataset.tapBound === 'true') return;

      dot.addEventListener('click', function (e) {
        // Only intercept the click on touch / mobile widths. On desktop the
        // hover state still drives the reveal and clicking the dot does
        // nothing special.
        if (!isMobile()) return;
        e.preventDefault();
        e.stopPropagation();

        var willOpen = !marker.classList.contains('is-open');
        closeAllInContainer(container, marker);
        marker.classList.toggle('is-open', willOpen);
        dot.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      });

      dot.dataset.tapBound = 'true';
    });

    // Tap outside any marker closes the currently-open one.
    if (container.dataset.outsideTapBound !== 'true') {
      document.addEventListener('click', function (e) {
        if (!isMobile()) return;
        if (e.target.closest('.hero-marker')) return;
        closeAllInContainer(container, null);
      });
      container.dataset.outsideTapBound = 'true';
    }
  }

  function initContainer(container) {
    if (!container || container.dataset.markersInitialised === 'true') return;

    var media = container.querySelector('[data-markers-image]') || container;
    var markers = container.querySelectorAll('.hero-marker');
    if (!markers.length) return;

    function positionMarkers() {
      markers.forEach(applyBreakpointAttrs);
      var rect = getImageVisibleRect(media);
      markers.forEach(function (marker) {
        var px = parseFloat(marker.dataset.activePosX) / 100;
        var py = parseFloat(marker.dataset.activePosY) / 100;
        if (isNaN(px) || isNaN(py)) return;
        var left = rect.offsetX + px * rect.visibleW;
        var top = rect.offsetY + py * rect.visibleH;
        marker.style.left = left + 'px';
        marker.style.top = top + 'px';
      });
      // After every marker has its pixel position, run the mobile
      // auto-flip pass so any direction overflow gets corrected before
      // the card animates open on tap. No-op on desktop.
      markers.forEach(function (marker) { autoFlipMobileDirection(marker, container); });
    }

    var img = media.querySelector('img');
    if (img && img.complete) {
      positionMarkers();
    } else if (img) {
      img.addEventListener('load', positionMarkers, { once: true });
    }

    // Debounced resize so we don't churn on every pixel of a window drag.
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(positionMarkers, 100);
    }, { passive: true });
    requestAnimationFrame(positionMarkers);

    bindTapToggles(container, markers);

    container.dataset.markersInitialised = 'true';
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var containers = scope.querySelectorAll('[data-markers-container]');
    containers.forEach(initContainer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target && event.target.querySelectorAll) {
      event.target.querySelectorAll('[data-markers-container]').forEach(function (c) {
        delete c.dataset.markersInitialised;
        delete c.dataset.outsideTapBound;
      });
    }
  });
})();
